/**
 * SuggestionsList - Sidebar panel listing all agent proposals for a trip.
 *
 * Renders:
 *   - Tab strip: Outstanding | All | Actioned (with count badges)
 *   - Scrollable list of SuggestionItem rows
 *   - Empty state when no proposals match the active tab
 *
 * Props:
 *   suggestions  - All suggestion docs from the real-time subscription
 *   onApprove    - (suggestionId) => void
 *   onDismiss    - (suggestionId) => void
 *   onModify     - (suggestionId, modifiedNote) => void
 *   canApprove   - boolean
 */

import React, { useState } from 'react';
import SuggestionItem from './SuggestionItem';
import './SuggestionsList.css';

const TABS = [
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'all',         label: 'All' },
  { id: 'actioned',    label: 'Actioned' }
];

function filterSuggestions(suggestions, tab) {
  switch (tab) {
    case 'outstanding':
      return suggestions.filter((s) => s.status === 'outstanding');
    case 'actioned':
      return suggestions.filter((s) => s.status !== 'outstanding');
    default:
      return suggestions;
  }
}

function countByTab(suggestions, tabId) {
  switch (tabId) {
    case 'outstanding':
      return suggestions.filter((s) => s.status === 'outstanding').length;
    case 'actioned':
      return suggestions.filter((s) => s.status !== 'outstanding').length;
    default:
      return suggestions.length;
  }
}

const SuggestionsList = ({
  suggestions = [],
  onApprove,
  onDismiss,
  onModify,
  canApprove = true
}) => {
  const [activeTab, setActiveTab] = useState('outstanding');

  const filtered = filterSuggestions(suggestions, activeTab);

  return (
    <div className="suggestions-list">
      {/* Tab strip */}
      <div className="suggestions-list__tabs" role="tablist">
        {TABS.map((tab) => {
          const count = countByTab(suggestions, tab.id);
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={[
                'suggestions-list__tab',
                activeTab === tab.id ? 'suggestions-list__tab--active' : ''
              ].join(' ')}
              onClick={() => setActiveTab(tab.id)}
              type="button"
            >
              {tab.label}
              {count > 0 && (
                <span className={[
                  'suggestions-list__badge',
                  tab.id === 'outstanding' && count > 0
                    ? 'suggestions-list__badge--highlight'
                    : ''
                ].join(' ')}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Items list */}
      <div className="suggestions-list__items" role="tabpanel">
        {filtered.length === 0 ? (
          <div className="suggestions-list__empty">
            {activeTab === 'outstanding'
              ? 'No outstanding suggestions'
              : activeTab === 'actioned'
              ? 'No actioned suggestions yet'
              : 'No suggestions yet. Ask the travel agent to suggest something!'}
          </div>
        ) : (
          filtered.map((suggestion) => (
            <SuggestionItem
              key={suggestion.id}
              suggestion={suggestion}
              onApprove={onApprove}
              onDismiss={onDismiss}
              onModify={onModify}
              canApprove={canApprove}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default SuggestionsList;
